<template>
  <b-nav-item-dropdown
    class="dropdown-notification mr-25"
    menu-class="dropdown-menu-media"
    right
    @hide="onHideNotification"
    @show="onShowNotification"
  >
    <template #button-content>
      <feather-icon
        :badge="unseenCount"
        badge-classes="bg-danger"
        class="text-body"
        icon="BellIcon"
        size="21"
      />
    </template>

    <!-- Notifications -->
    <div v-if="isShowDropdown">
      <!-- Header -->
      <li class="dropdown-menu-header">
        <div class="dropdown-header d-flex">
          <h4 class="notification-title mb-0 mr-auto">Notifications</h4>
          <b-badge v-if="!!unreadCount" pill variant="light-primary">
            {{ unreadCount }} Unread
          </b-badge>
        </div>
        <div v-if="isLoading" class="progress2 progress-moved">
          <div class="progress-bar2"></div>
        </div>
      </li>

      <!-- System Notifications -->
      <div class="dropdown__scroll">
        <div v-if="!systemNotifications.length" class="text-center p-1">
          <span>No Notifications.</span>
        </div>
        <b-link
          v-for="(notification, index) of systemNotifications"
          :key="index"
          @click="onClickNotification(notification)"
        >
          <b-media
            class="px-20px"
            :class="[
              { 'pt-14px': index === 0 },
              { 'pb-25px': index === systemNotifications.length - 1 },
            ]"
          >
            <template #aside>
              <b-avatar
                :badge="notification.isNew"
                badge-top
                badge-variant="danger"
                badge-offset="-3px"
                rounded
                size="32"
                :variant="getVariant(notification.status)"
              >
                <feather-icon :icon="getIcon(notification.type)" />
              </b-avatar>
            </template>
            <p class="media-heading">
              <span class="font-weight-bolder">
                {{ notification.title }}
              </span>
            </p>
            <small class="notification-subtitle">{{
              notification.subtitle
            }}</small>
            <div>
              <small class="notification-text">{{ notification.date }}</small>
            </div>
          </b-media>

          <hr v-if="index < systemNotifications.length - 1" />
        </b-link>
      </div>
    </div>
  </b-nav-item-dropdown>
</template>

<script>
import {
  getNotiConfig,
  getNoti,
  updateNotiStatusToSeen,
  updateNotiStatusToRead,
} from "@/api/api-module-noti"
import { DateTime } from "luxon"
import { BNavItemDropdown, BBadge, BMedia, BLink, BAvatar } from "bootstrap-vue"
import Ripple from "vue-ripple-directive"
import mixins from "@/mixins"
import { mapActions, mapGetters } from "vuex"

import notificationSound from "@/assets/sounds/noti-sound-forest.wav"

export default {
  name: "NotificationDropdownCustom",
  components: {
    BNavItemDropdown,
    BBadge,
    BMedia,
    BLink,
    BAvatar,
  },
  directives: {
    Ripple,
  },
  mixins: [mixins],
  data() {
    return {
      isLoading: false,
      isShowDropdown: false,
      unseenCount: 0,
      unreadCount: 0,
      refreshInterval: null,
      systemNotifications: [],
      perfectScrollbarSettings: [],

      intervalId: 0,

      latestNotification: null,
    }
  },
  computed: {
    ...mapGetters("appFilter", [
      "getInternalParcelTrackingFilter",
      "getExternalParcelTrackingFilter",
      "getInternalInventoryFilter",
      "getInternalIpAddressManagerFilter",
      "getExternalCrossConnectFilter",
    ]),

    ...mapGetters("commonStore", ["getNotificationSoundEnabled"]),

    isInternal() {
      return this.$route.meta.type === "internal"
    },

    isEnableNotiSound() {
      return this.getNotificationSoundEnabled
    },
  },
  async mounted() {
    await this.getNotiConfig()
    await this.getNotificationFromApi()

    if (this.refreshInterval !== null) {
      this.intervalId = window.setInterval(() => {
        this.getNotificationFromApi()
      }, this.refreshInterval * 1000)
    } else {
      window.clearInterval(this.intervalId)
    }
  },
  beforeDestroy() {
    clearInterval(this.intervalId)
  },
  methods: {
    ...mapActions("appFilter", [
      "setInternalParcelTrackingFilter",
      "setExternalParcelTrackingFilter",
      "setInternalInventoryFilter",
      "setInternalIpAddressManagerFilter",
      "setExternalCrossConnectFilter",
    ]),

    async getNotiConfig() {
      this.isLoading = true
      await getNotiConfig(this.isInternal)
        .then((noti) => {
          this.refreshInterval = noti.refreshInterval
        })
        .finally(() => (this.isLoading = false))
    },

    async getNotificationFromApi() {
      this.isLoading = true
      await getNoti(this.isInternal)
        .then((noti) => {
          this.unseenCount = noti.unseen
          this.unreadCount = noti.unread

          this.systemNotifications = noti.message.map((msg) => ({
            id: msg.notificationId,
            title: msg.subject,
            subtitle: msg.message,
            status: msg.status,
            date: DateTime.fromISO(msg.date).toFormat("dd MMM yyyy, HH:mm"),
            type: msg.type,
            isNew: msg.status === "NEW",
            link: msg.link,
          }))
        })
        .finally(() => {
          this.isLoading = false
          if (this.isEnableNotiSound) {
            if (
              this.latestNotification &&
              this.latestNotification?.id !== this.systemNotifications[0]?.id
            ) {
              this.playNotificationSound()
            }
          }
          this.latestNotification = this.systemNotifications[0] || null
        })
    },

    onShowNotification() {
      this.isShowDropdown = true
    },

    async onHideNotification() {
      this.isShowDropdown = false

      const notificationIds = this.systemNotifications
        .filter((noti) => noti.isNew)
        .map((noti) => noti.id)

      if (!!notificationIds.length) {
        await updateNotiStatusToSeen(notificationIds, this.isInternal)
          .then(() => {
            this.getNotificationFromApi()
          })
          .catch(() => {
            this.toastError("Update notification status failed")
          })
      }
    },

    getVariant(status) {
      if (["NEW", "SEEN"].includes(status)) return "light-primary"
      return "light-secondary"
    },

    getIcon(type) {
      if (type === "TK") return "TagIcon"
      if (type === "SH") return "ThumbsUpIcon"
      if (type === "PT") return "TruckIcon"
      if (type === "IPAM") return "HashIcon"
      if (type === "XC") return "LinkIcon"
      if (type === "IN") return "BriefcaseIcon"
      if (type === "TKE") return "ChevronsUpIcon"
      if (type === "QR" || type === "SA") return "MaximizeIcon"
      if (
        type === "NTDC" ||
        type === "NTDC_CONTRACT" ||
        type === "NTDC_CUSTOMER" ||
        type === "NTDC_CONTACT_POINT" ||
        type === "NTDC_ATTACHMENT"
      ) {
        return "DatabaseIcon"
      }
      return "XIcon"
    },

    async onClickNotification(notification) {
      this.isShowDropdown = false

      if (notification.status !== "READ") {
        await updateNotiStatusToRead([notification.id], this.isInternal)
          .then(() => {
            this.getNotificationFromApi()
          })
          .catch(() => {
            this.toastError("Update notification status failed")
          })
      }
      this.setFilterByType(notification.type, notification.link)
      this.handleNotificationLink(notification)
    },

    setFilterByType(type, link) {
      if (type === "PT") {
        const id = link.split("?id=")[1]

        if (this.isInternal) {
          this.setInternalParcelTrackingFilter({
            ...this.getInternalParcelTrackingFilter,
            trackingId: id,
          })
        } else {
          this.setExternalParcelTrackingFilter({
            ...this.getExternalParcelTrackingFilter,
            trackingId: id,
          })
        }
      }

      if (type === "IN") {
        const id = link.split("?id=")[1]

        this.setInternalInventoryFilter({
          ...this.getInternalInventoryFilter,
          id,
        })
      }

      if (type === "IPAM") {
        const subnetId = link.split("?id=")[1]
        const ip = link.split("&ip=")[1]

        this.setInternalIpAddressManagerFilter({
          ...this.getInternalIpAddressManagerFilter,
          subnetAddress: [{ subnetId }],
          subnetIpAddressOrDescription: ip,
        })
      }

      if (type === "XC") {
        const id = link.split("cross-connect/")[1]
        if (!this.isInternal) {
          this.setExternalCrossConnectFilter({
            ...this.getExternalCrossConnectFilter,
            xConnectId: id,
          })
        }
      }
    },

    handleNotificationLink(notification) {
      if (notification.type === "XC" && !this.isInternal) {
        const id = notification.link.split("cross-connect/")[1]
        this.$router.push({ name: "externalCrossConnect", query: { id } })
      } else {
        if (this.$route.fullPath !== notification.link) {
          this.$router.push(notification.link)
        }
      }
    },

    playNotificationSound() {
      const sound = new Audio(notificationSound)
      sound.play().catch((err) => {
        console.warn("Autoplay failed:", err)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.notification-subtitle {
  color: #6e6b7b;
}

::v-deep {
  .badge-danger {
    font-size: 7px !important;
  }
}

.px-20px {
  padding-left: 20px;
  padding-right: 20px;
}

.pt-14px {
  padding-top: 14px;
}

.pb-25px {
  padding-bottom: 25px;
}

.dropdown__scroll {
  width: 100%;
  max-height: calc(100vh - 200px);
  overflow: auto;
}

.progress2 {
  border-radius: 30px;
  background-color: #eeedfd;
}

.progress-bar2 {
  height: 3px;
  border-radius: 30px;
  transition: 0.4s linear;
  transition-property: width, background-color;
}

.progress-moved .progress-bar2 {
  animation: progress 2s infinite;
}

@keyframes progress {
  0% {
    width: 0%;
    background: #7367f0;
  }

  100% {
    width: 100%;
    background: #7367f0;
  }
}
</style>
